/**
 * Zod schema for the `Collection` + `CollectionMember` persistence shapes.
 *
 * Mirrors `../types/collection.ts`. Spec authority: `architecture.md §4.7`
 * `CREATE TABLE collections` + `CREATE TABLE collection_members`.
 */

import { z } from "zod";
import type { Collection, CollectionMember } from "../types/collection.js";
import { UUID_RE, HANDLE_SLUG_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const slug = z.string().regex(HANDLE_SLUG_RE);
const timestamp = z.number().int().nonnegative();

export const CollectionSchema = z
  .object({
    uuid,
    slug,
    name: z.string(),
    parentCollectionUuid: uuid.nullable().optional(),
    createdAt: timestamp,
  })
  .strict();

export const CollectionMemberSchema = z
  .object({
    collectionUuid: uuid,
    recipeUuid: uuid,
    addedAt: timestamp,
  })
  .strict();

type _AssertCollection =
  z.infer<typeof CollectionSchema> extends Collection
    ? Collection extends z.infer<typeof CollectionSchema>
      ? true
      : never
    : never;
type _AssertCollectionMember =
  z.infer<typeof CollectionMemberSchema> extends CollectionMember
    ? CollectionMember extends z.infer<typeof CollectionMemberSchema>
      ? true
      : never
    : never;
export type __CollectionSchemaAsserts = [_AssertCollection, _AssertCollectionMember];
